<template>
  <button
    :id="id"
    :type="buttonType"
    :class="buttonEffectClasses"
    class="content__item-action btn btn-lg"
    :disabled="isDisabled"
    @click="handleButtonClick"
  >
    <slot></slot>
    <DotsLoader />
  </button>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import useButtonEffects from '@/hooks/useButtonEffects';
import DotsLoader from '@/components/ui/DotsLoader.vue';
import { ActionType } from '@/typings/enums/enums';

type ButtonStyle = 'primary' | 'secondary';
type ButtonType = 'submit' | 'reset' | 'button';

export default defineComponent({
  name: 'UIButton',
  components: { DotsLoader },
  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    buttonType: {
      type: String as PropType<ButtonType>,
      default: 'button'
    },
    id: {
      type: String,
      required: false
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonStyle: {
      type: String as PropType<ButtonStyle>,
      default: 'primary'
    },
    preventStop: {
      type: String as PropType<ActionType>,
      default: ActionType.NONE
    }
  },
  emits: ['click'],
  setup(props, context) {
    const { applyButtonEffects } = useButtonEffects();

    const buttonId = computed(() => {
      if (typeof context.attrs.id === 'string') {
        return typeof context.attrs.id;
      } else {
        return undefined;
      }
    });

    const handleButtonClick = (e: MouseEvent) => {
      if (props.preventStop !== ActionType.NONE) {
        e.preventDefault();
        e.stopPropagation();
      }
      context.emit('click');
    };

    const buttonEffectClasses = computed(() => {
      let buttonEffect = '';
      if (props.buttonStyle === 'primary') {
        buttonEffect = applyButtonEffects('btn--primary');
      }
      if (props.buttonStyle === 'secondary') {
        buttonEffect = applyButtonEffects('btn--secondary');
      }
      return {
        [buttonEffect]: true,
        'btn--loading': props.isLoading,
        [`btn--${props.buttonStyle}`]: true,
        'content__item-action--disabled': props.isDisabled
      };
    });

    return {
      buttonEffectClasses,
      buttonId,
      handleButtonClick
    };
  }
});
</script>
